import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { dashboard, dashboardSummary, sessions, briefing, escalations, clientConversations, billing } from '../api/client'
import AppIcon from '../components/AppIcon'
import FloatingVoiceButton from '../components/FloatingVoiceButton'
import { PageLayout, Card, SkeletonDashboard, Badge } from '../components/ui'
import { formatTimeAgo, formatTime, getGreeting } from '../utils/date'
import { getChangeRating } from '../utils/constants'

// Icons as simple SVG components
function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function DumbbellIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h2m12 0h2M6 8v8m12-8v8M8 8h8m-8 8h8M6 12h2m8 0h2" />
    </svg>
  )
}

function ClipboardIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  )
}

function BookIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  )
}

function ChatIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  )
}

function ChatBubblesIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
    </svg>
  )
}

function CogIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function LightbulbIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  )
}

function MealIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      <circle cx="7" cy="11" r="2" strokeWidth={2} />
      <circle cx="17" cy="11" r="2" strokeWidth={2} />
    </svg>
  )
}

function MicrophoneIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  )
}

function PhotoIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
}

function AISettingsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  )
}

function MemoryIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  )
}

function CurrencyDollarIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function RocketIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  )
}

function ActivityFeedIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  )
}

function SunIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function MegaphoneIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  )
}

function TrophyIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3h14l-1.405 8.426A5 5 0 0112.63 16h-.26a5 5 0 01-4.965-4.574L6 3zM5 3H3v2a3 3 0 003 3M19 3h2v2a3 3 0 01-3 3M12 16v3m-4 2h8" />
    </svg>
  )
}

function AlertTriangleIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
    </svg>
  )
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ChevronIcon({ className, expanded }) {
  return (
    <svg className={`${className} transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

function StarRating({ rating }) {
  if (!rating) return null
  return (
    <span className="text-lume-teal">
      {'★'.repeat(Math.min(rating, 5))}{'☆'.repeat(Math.max(0, 5 - rating))}
    </span>
  )
}

function DailyBriefing() {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('forge_briefing_collapsed') === 'true'
  })

  const { data, isLoading } = useQuery({
    queryKey: ['dailyBriefing'],
    queryFn: briefing.get,
    staleTime: 5 * 60 * 1000,
  })

  const toggleCollapse = () => {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem('forge_briefing_collapsed', String(next))
  }

  if (isLoading || !data) return null

  const { todaySessions: bSessions, atRiskClients, wins, suggestedActions } = data
  const hasContent = (bSessions?.length > 0) || (atRiskClients?.length > 0) ||
    (wins?.length > 0) || (suggestedActions?.length > 0)

  if (!hasContent) return null

  return (
    <div className="px-4 pb-2">
      <Card padding="none">
        {/* Header */}
        <button
          onClick={toggleCollapse}
          className="w-full px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-dial-border"
        >
          <div className="flex items-center gap-2">
            <SunIcon className="w-5 h-5 text-primary-600 dark:text-lume-teal" />
            <h3 className="font-semibold text-gray-900 dark:text-indices-bright font-display">Daily Briefing</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-indices-muted">{data.date}</span>
            <ChevronIcon className="w-4 h-4 text-gray-400 dark:text-indices-muted" expanded={!collapsed} />
          </div>
        </button>

        {!collapsed && (
          <div className="divide-y divide-gray-200 dark:divide-dial-border">
            {/* Today's Sessions with Context */}
            {bSessions?.length > 0 && (
              <div className="p-4">
                <h4 className="text-xs font-medium text-gray-500 dark:text-indices-muted uppercase tracking-wider mb-2">
                  Today's Sessions
                </h4>
                <div className="space-y-2">
                  {bSessions.map(session => (
                    <div key={session.sessionId} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-16 text-right">
                        <p className="text-sm font-medium text-primary-600 dark:text-lume-teal font-display">
                          {session.time}
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {session.clientId ? (
                            <Link
                              to={`/clients/${session.clientId}`}
                              className="text-sm font-medium text-gray-900 dark:text-indices-bright hover:text-primary-600 dark:hover:text-lume-teal truncate"
                            >
                              {session.clientName}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium text-gray-900 dark:text-indices-bright truncate">
                              Session
                            </span>
                          )}
                          <span className="text-xs text-gray-500 dark:text-indices-muted">{session.sessionType}</span>
                        </div>
                        {session.context && (
                          <p className="text-xs text-gray-500 dark:text-indices-muted mt-0.5 truncate">
                            {session.context.currentWeight && (
                              <span className="text-primary-600 dark:text-lume-teal">{session.context.currentWeight}kg</span>
                            )}
                            {session.context.weightTrend && (
                              <span> ({session.context.weightTrend})</span>
                            )}
                            {session.context.lastWorkout && (
                              <span>
                                {session.context.currentWeight ? ' · ' : ''}
                                Last: {session.context.lastWorkout.name}
                                {session.context.lastWorkout.rating && (
                                  <> <StarRating rating={session.context.lastWorkout.rating} /></>
                                )}
                              </span>
                            )}
                            {session.context.criticalNotes?.length > 0 && (
                              <span className="text-status-warning"> · ⚠ {session.context.criticalNotes[0]}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Wins */}
            {wins?.length > 0 && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrophyIcon className="w-4 h-4 text-primary-600 dark:text-lume-teal" />
                  <h4 className="text-xs font-medium text-gray-500 dark:text-indices-muted uppercase tracking-wider">
                    Wins
                  </h4>
                </div>
                <div className="space-y-1">
                  {wins.map((win, i) => (
                    <Link
                      key={i}
                      to={`/clients/${win.clientId}`}
                      className="flex items-center gap-2 text-sm hover:bg-gray-50 dark:hover:bg-dial-elevated rounded px-1 py-0.5 -mx-1 transition-colors"
                    >
                      <span className="text-primary-600 dark:text-lume-teal font-medium truncate">{win.clientName}</span>
                      <span className="text-gray-500 dark:text-indices-muted truncate">{win.description}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* At-Risk Clients */}
            {atRiskClients?.length > 0 && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangleIcon className="w-4 h-4 text-status-warning" />
                  <h4 className="text-xs font-medium text-status-warning uppercase tracking-wider">
                    Needs Attention
                  </h4>
                </div>
                <div className="space-y-1">
                  {atRiskClients.map((client, i) => (
                    <Link
                      key={i}
                      to={`/clients/${client.clientId}`}
                      className="flex items-center justify-between text-sm hover:bg-gray-50 dark:hover:bg-dial-elevated rounded px-1 py-0.5 -mx-1 transition-colors"
                    >
                      <span className="text-gray-900 dark:text-indices-bright font-medium truncate">{client.clientName}</span>
                      <span className="text-xs text-gray-500 dark:text-indices-muted flex-shrink-0 ml-2">
                        {client.riskFactors?.[0]}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Actions */}
            {suggestedActions?.length > 0 && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircleIcon className="w-4 h-4 text-primary-600 dark:text-lume-teal" />
                  <h4 className="text-xs font-medium text-gray-500 dark:text-indices-muted uppercase tracking-wider">
                    Actions
                  </h4>
                </div>
                <div className="space-y-1">
                  {suggestedActions.map((item, i) => (
                    <Link
                      key={i}
                      to={`/clients/${item.clientId}`}
                      className="flex items-center justify-between text-sm hover:bg-gray-50 dark:hover:bg-dial-elevated rounded px-1 py-0.5 -mx-1 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          item.priority === 'high' ? 'bg-status-error' :
                          item.priority === 'medium' ? 'bg-status-warning' :
                          'bg-indices-muted'
                        }`} />
                        <span className="text-gray-900 dark:text-indices-bright truncate">
                          {item.action} — <span className="text-gray-500 dark:text-indices-muted">{item.clientName}</span>
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 dark:text-indices-muted flex-shrink-0 ml-2">{item.reason}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}

function StatCard({ label, value, subtitle }) {
  return (
    <Card>
      <p className="text-xs text-gray-500 dark:text-indices-muted">{label}</p>
      <p className="text-2xl font-bold text-primary-600 dark:text-lume-teal mt-1 font-display">{value ?? '-'}</p>
      {subtitle && <p className="text-xs text-gray-500 dark:text-indices-muted mt-1">{subtitle}</p>}
    </Card>
  )
}

// Get change rating display info (label and color classes)
function getChangeRatingInfo(rating) {
  const ratingInfo = getChangeRating(rating)
  if (!ratingInfo) {
    return { label: 'Analyzed', variant: 'default' }
  }
  const variantMap = {
    'bg-green-100': 'success',
    'bg-blue-100': 'info',
    'bg-gray-100': 'default',
    'bg-amber-100': 'warning',
  }
  return {
    label: ratingInfo.label,
    variant: variantMap[ratingInfo.bgClass] || 'default',
  }
}

export default function DashboardPage() {
  const { trainer } = useAuth()

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['trainerDashboard'],
    queryFn: dashboard.getData,
  })

  const { data: recentPhotos } = useQuery({
    queryKey: ['recentPhotoUpdates'],
    queryFn: dashboard.getRecentPhotoUpdates,
  })

  const { data: summaryData } = useQuery({
    queryKey: ['dashboardSummary'],
    queryFn: dashboardSummary.get,
  })

  const { data: todaySessions } = useQuery({
    queryKey: ['todaySessions'],
    queryFn: sessions.today,
  })

  const { data: escalationData } = useQuery({
    queryKey: ['escalationCount'],
    queryFn: escalations.count,
    refetchInterval: 60000, // refresh every minute
  })

  const { data: unreadData } = useQuery({
    queryKey: ['unreadConversationCount'],
    queryFn: clientConversations.unreadCount,
    refetchInterval: 30000, // refresh every 30s
  })

  const { data: outstandingData } = useQuery({
    queryKey: ['outstandingCount'],
    queryFn: billing.outstandingCount,
    refetchInterval: 60000, // refresh every minute
  })

  const firstName = trainer?.firstName || 'Trainer'
  const unreadEscalations = escalationData?.count || 0
  const unreadChats = unreadData?.unreadConversations || 0
  const chatBadge = unreadChats > 0 ? unreadChats : (unreadEscalations > 0 ? unreadEscalations : undefined)
  const photoUpdates = recentPhotos?.photos || []
  const outstandingInvoices = outstandingData?.count || 0

  if (isLoading) {
    return (
      <PageLayout>
        <SkeletonDashboard />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      {/* Header */}
      <div className="bg-white dark:bg-dial-surface border-b border-gray-200 dark:border-dial-border px-4 pt-12 pb-4 safe-area-inset-top">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-indices-bright font-display">
          {getGreeting()}, <span className="text-primary-600 dark:text-lume-teal lume-glow">{firstName}</span>
        </h1>
        <p className="text-gray-500 dark:text-indices-muted mt-1">
          {summaryData?.activeClients != null
            ? `${summaryData.activeClients} active clients • ${summaryData.todaysSessions || 0} sessions today`
            : 'Welcome to your dashboard'}
        </p>
      </div>

      {/* Stats Row */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            label="Clients"
            value={dashboardData?.activeClients}
            subtitle="Active"
          />
          <StatCard
            label="Today"
            value={dashboardData?.todaysSessions}
            subtitle="Sessions"
          />
          <StatCard
            label="Check-in"
            value={dashboardData?.clientsNeedingCheckIn}
            subtitle="Needed"
          />
        </div>
      </div>

      {/* Daily Intelligence Briefing */}
      <DailyBriefing />

      {/* Today's Schedule */}
      {todaySessions && todaySessions.length > 0 && (
        <div className="px-4 pb-2">
          <Card padding="none">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-dial-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary-600 dark:text-lume-teal" />
                <h3 className="font-semibold text-gray-900 dark:text-indices-bright font-display">Today's Schedule</h3>
              </div>
              <Link to="/schedule" className="text-xs text-primary-600 dark:text-lume-teal font-medium hover:underline">
                View All
              </Link>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-dial-border">
              {todaySessions.slice(0, 4).map(session => (
                <div key={session.id} className="flex items-center gap-3 p-3">
                  <div className="flex-shrink-0 w-12 text-center">
                    <p className="text-sm font-medium text-primary-600 dark:text-lume-teal font-display">
                      {formatTime(session.scheduledDate || session.scheduledAt)}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-indices-bright truncate">
                      {session.client ? `${session.client.firstName} ${session.client.lastName}` : 'Session'}
                    </p>
                    {session.sessionType && (
                      <p className="text-xs text-gray-500 dark:text-indices-muted capitalize">
                        {session.sessionType.replace('_', ' ')}
                      </p>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    session.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-status-success/20 dark:text-status-success' :
                    session.status === 'cancelled' ? 'bg-gray-100 text-gray-500 dark:bg-dial-elevated dark:text-indices-muted' :
                    session.status === 'no_show' ? 'bg-red-100 text-red-700 dark:bg-status-error/20 dark:text-status-error' :
                    'bg-blue-100 text-blue-700 dark:bg-status-info/20 dark:text-status-info'
                  }`}>
                    {session.status === 'scheduled' ? 'Upcoming' : session.status?.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Clients Needing Attention */}
      {summaryData?.clientsNeedingCheckIn > 0 && (
        <div className="px-4 pb-2">
          <Link to="/clients" className="block">
            <Card className="border-l-4 border-l-status-warning dark:border-l-status-warning">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-status-warning/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-status-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-indices-bright">
                    {summaryData.clientsNeedingCheckIn} client{summaryData.clientsNeedingCheckIn > 1 ? 's' : ''} need{summaryData.clientsNeedingCheckIn === 1 ? 's' : ''} check-in
                  </p>
                  <p className="text-xs text-gray-500 dark:text-indices-muted">Haven't been active recently</p>
                </div>
                <svg className="w-5 h-5 text-gray-400 dark:text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Card>
          </Link>
        </div>
      )}

      {/* Quick Stats Summary */}
      {summaryData?.quickStats && (
        <div className="px-4 pb-2">
          <Card>
            <h3 className="text-sm font-medium text-gray-500 dark:text-indices-muted mb-3 font-display">7-Day Activity</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-2 bg-gray-50 dark:bg-dial-elevated rounded-lg">
                <p className="text-xl font-bold text-primary-600 dark:text-lume-teal font-display">
                  {summaryData.quickStats.workoutsThisWeek || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-indices-muted">Workouts Completed</p>
              </div>
              <div className="text-center p-2 bg-gray-50 dark:bg-dial-elevated rounded-lg">
                <p className="text-xl font-bold text-primary-600 dark:text-lume-teal font-display">
                  {summaryData.quickStats.coachingNotesThisWeek || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-indices-muted">Coaching Notes</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* App Icons Grid */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-3 gap-4">
          <AppIcon
            to="/clients"
            icon={UsersIcon}
            label="Clients"
            color="bg-lume-teal"
          />
          <AppIcon
            to="/schedule"
            icon={CalendarIcon}
            label="Schedule"
            color="bg-status-info"
          />
          <AppIcon
            to="/exercises"
            icon={DumbbellIcon}
            label="Exercises"
            color="bg-lume-cream"
          />
          <AppIcon
            to="/workouts"
            icon={ClipboardIcon}
            label="Workouts"
            color="bg-status-success"
          />
          <AppIcon
            to="/meals"
            icon={MealIcon}
            label="Meal Plans"
            color="bg-teal-500"
          />
          <AppIcon
            to="/knowledge"
            icon={BookIcon}
            label="Knowledge"
            color="bg-purple-500"
          />
          <AppIcon
            to="/chat-activity"
            icon={ActivityFeedIcon}
            label="Chat Feed"
            color="bg-emerald-500"
          />
          <AppIcon
            to="/client-chats"
            icon={ChatBubblesIcon}
            label="Client Chats"
            color="bg-cyan-500"
            badge={chatBadge}
          />
          <AppIcon
            to="/chat"
            icon={ChatIcon}
            label="AI Chat"
            color="bg-pink-500"
          />
          <AppIcon
            to="/voice-chat"
            icon={MicrophoneIcon}
            label="Voice Chat"
            color="bg-indigo-500"
          />
          <AppIcon
            to="/feature-requests"
            icon={LightbulbIcon}
            label="Requests"
            color="bg-status-warning"
          />
          <AppIcon
            to="/ai-settings"
            icon={AISettingsIcon}
            label="AI Settings"
            color="bg-status-info"
          />
          <AppIcon
            to="/memory-log"
            icon={MemoryIcon}
            label="Memories"
            color="bg-amber-500"
          />
          <AppIcon
            to="/billing"
            icon={CurrencyDollarIcon}
            label="Billing"
            color="bg-green-600/20"
            badge={outstandingInvoices > 0 ? outstandingInvoices : undefined}
          />
          <AppIcon
            to="/announcements"
            icon={MegaphoneIcon}
            label="Broadcast"
            color="bg-amber-500"
          />
          <AppIcon
            to="/release-log"
            icon={RocketIcon}
            label="Releases"
            color="bg-violet-500"
          />
          <AppIcon
            to="/profile"
            icon={CogIcon}
            label="Settings"
            color="bg-indices-muted"
          />
        </div>
      </div>

      {/* Recent Photo Updates Widget */}
      {photoUpdates.length > 0 && (
        <div className="px-4 pb-4">
          <Card padding="none">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-dial-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PhotoIcon className="w-5 h-5 text-primary-600 dark:text-lume-teal" />
                <h3 className="font-semibold text-gray-900 dark:text-indices-bright font-display">Recent Photo Updates</h3>
              </div>
              <span className="text-xs text-gray-500 dark:text-indices-muted">{photoUpdates.length} analyzed</span>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-dial-border">
              {photoUpdates.slice(0, 5).map(photo => {
                const ratingInfo = getChangeRatingInfo(photo.changeRating)
                return (
                  <Link
                    key={photo.id}
                    to={`/clients/${photo.clientId}`}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-dial-elevated transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-indices-bright truncate">
                        {photo.clientName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-indices-muted truncate">
                        {photo.aiSummary || `${photo.photoTypeDisplay} photo analyzed`}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge variant={ratingInfo.variant} size="sm">
                        {ratingInfo.label}
                      </Badge>
                      <p className="text-[10px] text-gray-400 dark:text-indices-muted mt-0.5">
                        {formatTimeAgo(photo.analyzedAt)}
                      </p>
                    </div>
                  </Link>
                )
              })}
            </div>
          </Card>
        </div>
      )}
      <FloatingVoiceButton />
    </PageLayout>
  )
}
